/**
 * C-01 → C-07 ตาม docs/COMPONENTS.md §1 — สีทุกค่ามาจาก tokens.gen.css (var(--x)) ห้ามฝัง hex ที่นี่
 */
import { formatTHB, html, type Raw } from "../h.js";

/* ---------------- C-01 Money ---------------- */
export type MoneySize = "hero" | "lg" | "md" | "sm";
export function Money(p: { amount: number; size?: MoneySize; showDecimals?: boolean; currency?: string }): Raw {
  const size = p.size ?? "md";
  const neg = p.amount < 0;
  const text = (neg ? "−" : "") + formatTHB(Math.abs(p.amount), p.showDecimals ?? true);
  return html`<span class="money money-${size}${neg ? " money-neg" : ""}">${text}<small>${p.currency ?? "บาท"}</small></span>`;
}

/* ---------------- C-02 StatusTag ---------------- */
/** ค่าจาก `v_receipts.verify_status` (+ duplicate จากท่อ) — **ห้ามแสดงสีโดยไม่มีข้อความ** */
export type Status = "bank" | "image" | "pending" | "rejected" | "duplicate";
export const STATUS_TEXT: Record<Status, string> = {
  bank: "ยืนยันแล้ว",
  image: "อ่านจากภาพ",
  pending: "รอตรวจ",
  rejected: "ปฏิเสธ",
  duplicate: "สลิปซ้ำ",
};
const STATUS_TONE: Record<Status, "seal" | "amber" | "stamp"> = {
  bank: "seal", image: "amber", pending: "amber", rejected: "stamp", duplicate: "stamp",
};
export function StatusTag(p: { status: Status }): Raw {
  return html`<span class="tag tag-${STATUS_TONE[p.status]}" data-status="${p.status}">${STATUS_TEXT[p.status]}</span>`;
}

/* ---------------- C-03 Eyebrow ---------------- */
export function Eyebrow(p: { text: string; number?: string | number }): Raw {
  return html`<div class="eyebrow">${p.number !== undefined ? html`<span class="eyebrow-n">${p.number}</span>` : null}<span>${p.text}</span><i></i></div>`;
}

/* ---------------- C-04 Button ---------------- */
export type ButtonVariant = "primary" | "ghost" | "seal" | "amber" | "line";
export function Button(p: {
  label: string; variant?: ButtonVariant; full?: boolean; loading?: boolean; disabled?: boolean; action?: string; type?: "button" | "submit";
}): Raw {
  const v = p.variant ?? "primary";
  return html`<button type="${p.type ?? "button"}" class="btn btn-${v}${p.full === false ? "" : " btn-full"}${p.loading ? " btn-loading" : ""}"
    ${p.disabled || p.loading ? "disabled" : ""} ${p.action ? html`data-action="${p.action}"` : null} aria-busy="${p.loading ? "true" : "false"}">
    <span class="btn-label">${p.label}</span><span class="btn-spin" aria-hidden="true"></span></button>`;
}

/* ---------------- C-05 Alert ---------------- */
export type AlertTone = "info" | "warn" | "danger";
/** แถบในหน้า — ห้ามเป็น modal ห้ามเป็น toast ที่หายเอง */
export function Alert(p: { tone: AlertTone; title: string; body?: string; action?: { label: string; action: string } }): Raw {
  return html`<div class="alert alert-${p.tone}" role="status">
    <div class="alert-title">${p.title}</div>
    ${p.body ? html`<div class="alert-body">${p.body}</div>` : null}
    ${p.action ? html`<button type="button" class="alert-action" data-action="${p.action.action}">${p.action.label}</button>` : null}
  </div>`;
}

/* ---------------- C-06 MeterBar ---------------- */
export function MeterBar(p: { value: number; max: number; tone?: "carbon" | "amber" | "stamp"; label?: string }): Raw {
  const pct = p.max > 0 ? Math.max(0, Math.min(100, (p.value / p.max) * 100)) : 0;
  return html`<div class="meter meter-${p.tone ?? "carbon"}" role="meter" aria-valuemin="0" aria-valuemax="${p.max}" aria-valuenow="${p.value}" ${p.label ? html`aria-label="${p.label}"` : null}><i style="width:${pct.toFixed(1)}%"></i></div>`;
}

/* ---------------- C-07 EmptyState ---------------- */
/** ต้องมี action เสมอ — ไม่มีสถานะว่างที่ไร้ทางออก (design-system §6 ข้อ 7) */
export function EmptyState(p: { icon: string; title: string; body?: string; action: { label: string; action: string } }): Raw {
  return html`<div class="empty">
    <div class="empty-icon" aria-hidden="true">${p.icon}</div>
    <h3 class="empty-title">${p.title}</h3>
    ${p.body ? html`<p class="empty-body">${p.body}</p>` : null}
    ${Button({ label: p.action.label, action: p.action.action, variant: "primary", full: false })}
  </div>`;
}
